import React, { useState, useEffect, useRef, useCallback, useMemo, type ComponentProps } from 'react';

import {
  ShoppingCartOutlined,
  UserOutlined,
  FileTextOutlined,
  PaperClipOutlined,
  ExportOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import SelectNotFoundContent from '@components/shared/ui/SelectNotFoundContent';
import OrderAttachmentsUpload from '@components/shared/orders/OrderAttachmentsUpload';
import OrderItemsForm from '@components/shared/orders/OrderItemsForm';
import FormReceiverInformation from '@components/shared/orders/FormReceiverInformation';
import OrderNameField from '@components/shared/orders/OrderNameField';
import OrderOptionsCard from '@components/shared/orders/OrderOptionsCard';
import OrderItemsCard from '@components/shared/orders/OrderItemsCard';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { usePlan } from '@contexts/operator/PlanContext';
import { useTour } from '@contexts/TourContext';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import useSmartBack from '@hooks/shared/useSmartBack';
import { useOrderNameAvailability } from '@hooks/orders/useOrderNameAvailability';
import { useShippingTypeBehavior } from '@hooks/orders/useShippingTypeBehavior';
import { useItemMode, ITEM_MODE_PRECISE } from '@hooks/orders/useItemMode';
import { useOrderBundles } from '@hooks/orders/useOrderBundles';
import { useInventoryAttributeDefinitions } from '@hooks/orders/useInventoryAttributeDefinitions';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import { buildOrderPayload, type OrderNewFormValues } from '@utils/orders/buildOrderPayload';
import { inventoryService as adminInventoryService } from '@services/admin/inventoryService';
import { inventoryService as operatorInventoryService } from '@services/operator/inventoryService';
import {
  Input,
  Select,
  Button,
  Card,
  Row,
  Col,
  Space,
  message,
  Spin,
  Collapse,
  Form,
} from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type {
  ApiEnvelope,
  ShippingTypeOption,
  SalesChannelOption,
  InventoryTypeOption,
  MerchantOption,
  WarehouseOption,
  CurrencyOption,
  OrderRole,
} from '@components/shared/orders/types';

const { Option } = Select;
const { Panel } = Collapse;

// Methods OrderNew calls directly. Pass-through methods (e.g. orderService.update)
// aren't listed here — child components are responsible for their own types.
interface OrderServiceLike {
  createOrder: (payload: Record<string, unknown>) => Promise<ApiEnvelope<{ id?: string }>>;
  // operator 端才有附件功能；admin orderService 不提供，故為 optional
  createAttachment?: (orderId: string, file: File) => Promise<ApiEnvelope<{ id?: string }>>;
  checkOrderNameAvailability: (
    orderName: string,
  ) => Promise<{ exists: boolean; orderId: string | null; error: Error | null }>;
  suggestName: (merchantId?: string | null) => Promise<ApiEnvelope<{ name?: string }>>;
  [key: string]: unknown;
}

// Pass-through services — typed by their consuming child component. Re-derive
// here from each child's Props so we stay structurally aligned without
// duplicating their interfaces.
type ContactAddressServiceLike = ComponentProps<typeof FormReceiverInformation>['contactAddressService'];
type ProductServiceLike = ComponentProps<typeof OrderItemsForm>['productService'];
type BundleServiceLike = ComponentProps<typeof OrderItemsForm>['bundleService'];

interface OrderNewProps {
  orderService: OrderServiceLike;
  contactAddressService: ContactAddressServiceLike;
  productService: ProductServiceLike;
  role?: OrderRole;
  salesChannels?: SalesChannelOption[];
  onReloadSalesChannels?: () => void | Promise<unknown>;
  shippingTypes?: ShippingTypeOption[];
  onReloadShippingTypes?: () => void | Promise<unknown>;
  inventoryTypes?: InventoryTypeOption[];
  merchantDataLoading?: boolean;
  currentMerchantId?: string | null;
  /** For operator role to select merchant */
  merchantOptions?: MerchantOption[];
  /** For admin role to select warehouse */
  warehouseOptions?: WarehouseOption[];
  /** Merchant's default warehouse */
  defaultWarehouseId?: string | null;
  defaultCurrency?: string;
  /** Currency list for CurrencySelect */
  currencies?: CurrencyOption[];
  enabledOrderItemColumns?: string[];
  availableOrderItemColumns?: string[];
  onUpdateOrderItemColumns?: (columns: string[]) => Promise<boolean> | boolean | void;
  bundleService?: BundleServiceLike;
}

type SubmitValuesResult =
  | ApiEnvelope<{ id?: string }>
  | { __aborted: true; success?: undefined };

interface RenderStepOptions {
  moreInfoKey?: string;
  createLink?: { url: string; textKey: string } | null;
}

interface OrderItemFormItem {
  key: number;
  product_id: string | { value: string; label: string } | null;
  sku?: string;
  name?: string;
  inventory_type_id: { value: string; label: string } | null;
  quantity: number;
  expiration_date: dayjs.Dayjs | null;
  manufacturing_date: dayjs.Dayjs | null;
  batch?: string;
  note?: string;
  price: number | null;
  identifier?: string;
  original_price: number | null;
  cost: number | null;
  custom_attributes?: Record<string, unknown>;
  inventory_id?: string | { value: string; label: string } | null;
  bundle_id?: string;
  bundle_key?: string;
  bundle_per_set_qty?: number;
  [extra: string]: unknown;
}

const OrderNew: React.FC<OrderNewProps> = ({
  orderService,
  contactAddressService,
  productService,
  role = 'admin',
  salesChannels = [],
  onReloadSalesChannels,
  shippingTypes = [],
  onReloadShippingTypes,
  inventoryTypes = [],
  merchantDataLoading = false,
  currentMerchantId = null,
  warehouseOptions = [],
  defaultWarehouseId = null,
  defaultCurrency = 'TWD',
  currencies,
  enabledOrderItemColumns = [],
  availableOrderItemColumns = [],
  onUpdateOrderItemColumns,
  bundleService,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const operatorData = useOperatorData();
  const { plan } = usePlan();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // 區分「儲存並新增下一筆」的 loading，避免兩顆按鈕同時轉 spinner
  const [savingAndAdding, setSavingAndAdding] = useState(false);

  // Tour setup
  let tourContext: { registerTour?: (steps: unknown[]) => void } = {};
  try {
    tourContext = useTour() as { registerTour?: (steps: unknown[]) => void };
  } catch (e) {
    // ignore
  }
  const { registerTour } = tourContext;

  const nameRef = useRef<HTMLDivElement | null>(null);
  const shippingTypeRef = useRef<HTMLDivElement | null>(null);
  const salesChannelRef = useRef<HTMLDivElement | null>(null);
  const receiverRef = useRef<HTMLDivElement | null>(null);
  const productsRef = useRef<HTMLDivElement | null>(null);
  const saveRef = useRef<HTMLButtonElement | null>(null);

  const nameValue = Form.useWatch('name', form);
  const shippingTypeValue = Form.useWatch('shipping_type_id', form);
  const salesChannelValue = Form.useWatch('sales_channel_id', form);
  const watchedMerchantId = Form.useWatch('merchant_id', form);
  const watchedOrderItems = Form.useWatch('order_items', form) as Array<{ inventory_id?: unknown }> | undefined;

  const {
    showCod,
    showDeliveryWithPickup,
    handleShippingTypeChange,
    filteredShippingTypes,
  } = useShippingTypeBehavior(form, shippingTypes);

  const renderStepContent = (
    titleKey: string,
    descriptionKey: string,
    value: unknown,
    options: RenderStepOptions = {},
  ) => {
    const { moreInfoKey, createLink } = options;
    return (
      <div>
        <div>{t(descriptionKey)}</div>
        {moreInfoKey && (
          <div style={{ marginTop: 8 }}>
            <Collapse ghost size="small">
              <Panel header={t('common.tour.learn_more')} key="1">
                <div style={{ fontSize: '12px', color: '#666' }}>{t(moreInfoKey)}</div>
              </Panel>
            </Collapse>
          </div>
        )}
        {createLink && (
          <div style={{ marginTop: 8 }}>
            <a href={createLink.url} target="_blank" rel="noopener noreferrer">
              {t(createLink.textKey)} <ExportOutlined />
            </a>
          </div>
        )}
        {!value && (
          <div style={{ color: '#ff4d4f', marginTop: 8, fontSize: '12px' }}>
            {t('admin.orders.tour.fill_to_continue')}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (!registerTour) return;

    const steps = [
      {
        title: t('admin.orders.tour.name.title'),
        description: renderStepContent('admin.orders.tour.name.title', 'admin.orders.tour.name.description', nameValue),
        target: () => nameRef.current,
        nextButtonProps: { disabled: !nameValue },
      },
      {
        title: t('admin.orders.tour.shipping_type.title'),
        description: renderStepContent('admin.orders.tour.shipping_type.title', 'admin.orders.tour.shipping_type.description', shippingTypeValue),
        target: () => shippingTypeRef.current,
        nextButtonProps: { disabled: !shippingTypeValue },
      },
      {
        title: t('admin.orders.tour.sales_channel.title'),
        description: renderStepContent(
          'admin.orders.tour.sales_channel.title',
          'admin.orders.tour.sales_channel.description',
          salesChannelValue,
          {
            moreInfoKey: 'admin.orders.tour.sales_channel.more_info',
            createLink: role === 'admin' ? {
              url: '/admin/sales_channels/new',
              textKey: 'admin.orders.tour.sales_channel.create_link',
            } : null,
          },
        ),
        target: () => salesChannelRef.current,
        nextButtonProps: { disabled: !salesChannelValue },
      },
      {
        title: t('admin.orders.tour.receiver_info.title'),
        description: t('admin.orders.tour.receiver_info.description'),
        target: () => receiverRef.current,
      },
      {
        title: t('admin.orders.tour.products.title'),
        description: t('admin.orders.tour.products.description'),
        target: () => productsRef.current,
      },
      {
        title: t('admin.orders.tour.save.title'),
        description: t('admin.orders.tour.save.description'),
        target: () => saveRef.current,
      },
    ];

    registerTour(steps);
    return () => registerTour([]);
  }, [registerTour, t, nameValue, shippingTypeValue, salesChannelValue]);

  // 訂單名稱可用性檢查（防抖 800ms）
  const {
    status: nameCheckStatus,
    existingOrderId,
    onNameChange: triggerNameCheck,
    reset: resetNameCheck,
  } = useOrderNameAvailability({
    checkAvailability: orderService.checkOrderNameAvailability,
    debounceMs: 800,
  });
  const [suggesting, setSuggesting] = useState(false);

  const handleSuggestName = async () => {
    try {
      setSuggesting(true);
      const res = await orderService.suggestName(watchedMerchantId);
      if (res.success) {
        form.setFieldsValue({ name: res.data?.name });
      } else {
        handleApiError(res, { t });
      }
    } catch (error) {
      handleNetworkError(error, { t });
    } finally {
      setSuggesting(false);
    }
  };

  const addItemRef = useRef<((item: Record<string, unknown>) => void) | null>(null);
  const removeItemRef = useRef<((index: number | number[]) => void) | null>(null);

  const handleAddItem = useCallback(() => {
    if (!addItemRef.current) return;
    addItemRef.current({
      key: Date.now(),
      product_id: null,
      sku: '',
      name: '',
      inventory_type_id: inventoryTypes[0] ? { value: inventoryTypes[0].id, label: inventoryTypes[0].name } : null,
      quantity: 1,
      expiration_date: null,
      manufacturing_date: null,
      batch: '',
      note: '',
      price: null,
      identifier: '',
      original_price: null,
      cost: null,
      custom_attributes: {},
    } as OrderItemFormItem as unknown as Record<string, unknown>);
    setTimeout(() => {
      const cards = document.querySelectorAll('.product-item-card');
      cards[cards.length - 1]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }, [inventoryTypes]);

  const [attachments, setAttachments] = useState<File[]>([]);

  const { itemMode, handleModeChange } = useItemMode(form);

  // Precise mode 的 warehouseId（admin 端需要，operator 端由後端自動處理）
  const preciseWarehouseId = useMemo(() => {
    if (role === 'operator') return null; // operator 端後端自動用 current_warehouse
    return form.getFieldValue('warehouse_id') || defaultWarehouseId;
  }, [role, defaultWarehouseId, form]);

  const activeInventoryService = role === 'admin' ? adminInventoryService : operatorInventoryService;

  // Precise mode: 任一 item 缺 inventory_id
  const preciseModeIncomplete = itemMode === ITEM_MODE_PRECISE &&
    Array.isArray(watchedOrderItems) &&
    watchedOrderItems.length > 0 &&
    watchedOrderItems.some(item => !item?.inventory_id);

  // Admin 不渲染 `<Form.Item name="merchant_id">`，merchant_id 只進得了 initialValues；
  // 而 currentMerchantId 來自 useMerchant() 是 async-loaded，mount 時可能還是 null，
  // 之後 initialValues 不會再 re-flow → `Form.useWatch('merchant_id')` 在 admin 永遠 undefined。
  // Operator 端 currentMerchantId 為 null，回退到 watchedMerchantId（由下方 useEffect 寫進 form）。
  const inventoryAttributeDefinitions = useInventoryAttributeDefinitions(
    role,
    currentMerchantId || watchedMerchantId,
  );

  const {
    bundleGroups,
    setBundleGroups,
    handleBundleSetQuantityChange,
    handleRemoveBundle,
    handleBundleSelectFromCard,
  } = useOrderBundles({ form, addItemRef, removeItemRef, inventoryTypes });

  // 銷售通路空選項時的引導內容
  const salesChannelNotFoundContent = (
    <SelectNotFoundContent
      hint={t('shared.orders.form.no_sales_channels_hint')}
      settingsPath={role === 'operator' ? '/operator/data/sales_channels' : '/admin/sales_channels'}
      settingsLabel={t('shared.orders.form.go_to_sales_channel_settings')}
      onReload={onReloadSalesChannels}
    />
  );

  // 物流方式空選項時的引導內容
  const shippingTypeNotFoundContent = role === 'operator' ? (
    <SelectNotFoundContent
      hint={t('shared.orders.form.no_shipping_types_hint')}
      settingsPath={plan === 'standard' ? '/operator/shipping_accounts' : '/operator/system_settings/merchant_shipping_types'}
      settingsLabel={plan === 'standard' ? t('shared.orders.form.go_to_shipping_account_settings') : t('shared.orders.form.go_to_shipping_type_settings')}
      onReload={onReloadShippingTypes}
    />
  ) : null;

  // Sync selectedMerchantId and default currency with operator context if it changes
  useEffect(() => {
    if (role === 'operator' && operatorData?.merchant?.id) {
      const newValues: Record<string, unknown> = { merchant_id: operatorData.merchant.id };

      // If merchant has a default currency and the form's currency is still the default 'TWD', update it
      const currentCurrency = form.getFieldValue('currency');
      if (operatorData.merchant.default_currency && (!currentCurrency || currentCurrency === 'TWD')) {
        newValues.currency = operatorData.merchant.default_currency;
      }

      form.setFieldsValue(newValues);
    }
  }, [role, operatorData?.merchant?.id, operatorData?.merchant?.default_currency, form]);

  // 核心提交邏輯：呼叫 createOrder 並回傳 response 給呼叫者決定後續行為
  // pre-flight 驗證（重複名稱）以 __aborted sentinel 統一處理
  const submitValues = async (
    values: OrderNewFormValues,
    options: { isDraft?: boolean } = {},
  ): Promise<SubmitValuesResult> => {
    if (nameCheckStatus === 'unavailable') {
      message.error(t(`shared.orders.form.messages.order_name_unavailable`) as string);
      return { __aborted: true };
    }
    const payload = buildOrderPayload(values, {
      role,
      operatorMerchantId: operatorData?.merchant?.id ?? null,
      isDraft: options.isDraft,
    });
    return await orderService.createOrder(payload as unknown as Record<string, unknown>);
  };

  // 建單成功後逐筆上傳附件。
  // OrderCreationService 的 JSON 契約不變 — 附件在訂單建立成功後才以獨立 endpoint 上傳。
  // 單筆附件上傳失敗不阻斷流程，只提示使用者，避免訂單已建立卻整體報錯。
  const uploadAttachments = async (orderId: string) => {
    if (!orderService.createAttachment) return;
    const files = attachments;
    if (files.length === 0) return;

    let failed = 0;
    for (const file of files) {
      try {
        const res = await orderService.createAttachment(orderId, file);
        if (!res?.success) failed += 1;
      } catch {
        failed += 1;
      }
    }
    if (failed > 0) {
      message.warning(t(`shared.orders.attachments.upload_partial_failed`, { count: failed }) as string);
    }
  };

  // 重置儲存並新增下一筆相關的所有非 form 狀態
  const resetNonFormState = () => {
    setAttachments([]);
    setBundleGroups({});
    resetNameCheck();
  };

  // 主要提交：成功後導向 show 頁
  const handleSubmit = async (isDraft = false) => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      try {
        const data = await submitValues(values, { isDraft });
        if ('__aborted' in data && data.__aborted) return;

        if (data.success) {
          let successMessage: string = t(`common.message.create_success`) as string;
          if (isDraft) {
            successMessage = t(`shared.orders.form.messages.create_draft_success`, {
              scheduledDate: values.scheduled_shipping_date && (values.scheduled_shipping_date as dayjs.Dayjs).format
                ? (values.scheduled_shipping_date as dayjs.Dayjs).format('YYYY-MM-DD')
                : '',
            }) as string;
          }
          message.success(successMessage);
          // 導向新訂單的 show 頁，避免返回快取的列表頁看不到新資料
          // React Router basename 會自動補上 /operator 或 /admin 前綴
          const recordId = data.data?.id;
          if (recordId) {
            await uploadAttachments(recordId);
            navigate(`/orders/${recordId}`, { replace: true });
          } else {
            // 後端理論上會回 id；若沒有則退回上一頁
            goBack();
          }
        } else {
          handleApiError(data, { t, fallbackMessage: t(`common.error.create_failed`) as string });
        }
      } catch (error) {
        console.error('Failed to create order:', error);
        handleNetworkError(error, { t });
      } finally {
        setLoading(false);
      }
    } catch (error) {
      // validateFields 拒絕：antd 已自動高亮錯誤欄位
      const errFields = (error as { errorFields?: Array<{ name: unknown }> })?.errorFields;
      if (errFields) {
        message.error(t(`common.message.please_check_form`) as string);
        if (errFields.length > 0) {
          form.scrollToField(errFields[0].name as never, { behavior: 'smooth', block: 'center' });
        }
      }
    }
  };

  // 儲存並新增下一筆：成功後重置表單留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      setSavingAndAdding(true);
      try {
        const data = await submitValues(values);
        if ('__aborted' in data && data.__aborted) return;

        if (data.success) {
          // 先上傳本筆附件，再重置表單（resetNonFormState 會清空 attachments state）
          if (data.data?.id) {
            await uploadAttachments(data.data.id);
          }
          message.success(t(`common.message.create_success`) as string);
          // 重置 antd form（含 order_items Form.List）+ 所有外部 useState
          form.resetFields();
          resetNonFormState();
        } else {
          handleApiError(data, { t, fallbackMessage: t(`common.error.create_failed`) as string });
        }
      } catch (error) {
        console.error('Failed to create order:', error);
        handleNetworkError(error, { t });
      } finally {
        setSavingAndAdding(false);
      }
    } catch (error) {
      const errFields = (error as { errorFields?: Array<{ name: unknown }> })?.errorFields;
      if (errFields) {
        message.error(t(`common.message.please_check_form`) as string);
        if (errFields.length > 0) {
          form.scrollToField(errFields[0].name as never, { behavior: 'smooth', block: 'center' });
        }
      }
    }
  };

  if (merchantDataLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip={t('common.loading')} />
      </div>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
      initialValues={{
        currency: defaultCurrency || 'TWD',
        warehouse_id: defaultWarehouseId,
        merchant_id: role === 'operator' ? operatorData?.merchant?.id : currentMerchantId,
        // total_amount NOT NULL — 對齊 schema default 0，使用者沒輸入時送 0 而非 nil
        total_amount: 0,
      }}
    >
      <div>
        <Row gutter={24}>
          {/* 左側欄位 */}
          <Col xs={24} lg={14}>
            {/* 基本資訊 */}
            <Card
              title={
                <Space>
                  <FileTextOutlined />
                  <span>{t(`shared.orders.form.sections.basic_info`)}</span>
                </Space>
              }
              style={{ marginBottom: 24 }}
            >
              {/* Operator: Merchant selection handled via Header if create, else hidden or read only in form if needed for validation */}
              {role === 'operator' && (
                <Form.Item name="merchant_id" hidden>
                  <Input />
                </Form.Item>
              )}

              {/* Admin: 選擇出貨倉庫 (只有多個倉庫時才顯示) */}
              {role === 'admin' && warehouseOptions.length > 1 && (
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item name="warehouse_id" label={t('models.order.warehouse')}>
                      <Select
                        placeholder={t('common.select.please_select')}
                        showSearch
                        optionFilterProp="children"
                        allowClear
                      >
                        {warehouseOptions.map((warehouse) => (
                          <Option key={warehouse.id} value={warehouse.id}>
                            {warehouse.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <Row gutter={16}>
                <Col span={12}>
                  <OrderNameField
                    role={role}
                    status={nameCheckStatus}
                    existingOrderId={existingOrderId}
                    onChange={triggerNameCheck}
                    onSuggest={handleSuggestName}
                    suggesting={suggesting}
                    disabled={loading}
                    anchorRef={nameRef}
                    testId="order-name-input"
                  />
                </Col>
                <Col span={12}>
                  <Form.Item name="extra_name" label={t('models.order.extra_name')}>
                    <Input placeholder={t('models.order.extra_name') as string} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <div ref={shippingTypeRef} style={{ width: '100%' }}>
                    <Form.Item
                      name="shipping_type_id"
                      label={t(`models.order.shipping_type`)}
                      rules={[{ required: true, message: t('common.validation.required') as string }]}
                    >
                      <Select
                        data-testid="order-shipping-type-select"
                        placeholder={t('common.select.please_select')}
                        showSearch
                        onChange={handleShippingTypeChange}
                        notFoundContent={shippingTypeNotFoundContent}
                        filterOption={(input, option) =>
                          (String(option?.children ?? '')).toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        {filteredShippingTypes.map((type) => (
                          <Option key={type.id} value={type.id}>
                            {t(`shipping_types.${type.code}`, { defaultValue: type.name })}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>
                </Col>
                <Col span={12}>
                  <div ref={salesChannelRef} style={{ width: '100%' }}>
                    <Form.Item
                      name="sales_channel_id"
                      label={t(`models.order.sales_channel`)}
                      rules={[{ required: true, message: t('common.validation.required') as string }]}
                    >
                      <Select
                        data-testid="order-sales-channel-select"
                        placeholder={t('common.select.please_select')}
                        showSearch
                        optionFilterProp="label"
                        notFoundContent={salesChannelNotFoundContent}
                        options={salesChannels.map((channel) => ({
                          value: channel.id,
                          label: channel.name,
                        }))}
                      />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
            </Card>

            {/* 收件人資訊 */}
            <Card
              title={
                <div ref={receiverRef}>
                  <Space>
                    <UserOutlined />
                    <span>{t(`shared.orders.form.sections.receiver_info`)}</span>
                  </Space>
                </div>
              }
              style={{ marginBottom: 24 }}
            >
              <FormReceiverInformation
                contactAddressService={contactAddressService}
                form={form}
                merchantId={(role === 'operator' ? operatorData?.merchant?.id : (currentMerchantId || watchedMerchantId)) || undefined}
              />
            </Card>

            {/* 附件 */}
            <Card
              title={
                <Space>
                  <PaperClipOutlined />
                  <span>{t(`shared.orders.form.sections.attachments`)}</span>
                </Space>
              }
              style={{ marginBottom: 24 }}
            >
              <OrderAttachmentsUpload
                value={attachments}
                onChange={setAttachments}
              />
            </Card>
          </Col>

          {/* 右側欄位 */}
          <Col xs={24} lg={10}>
            <OrderOptionsCard
              showCod={showCod}
              showDeliveryWithPickup={showDeliveryWithPickup}
              currencies={currencies}
              disablePastDates
            />
          </Col>
        </Row>

        {/* 商品清單 - 全版展開 */}
        <OrderItemsCard
          itemMode={itemMode}
          onModeChange={handleModeChange}
          required
          titleAnchorRef={productsRef}
          addItemRef={addItemRef}
          removeItemRef={removeItemRef}
          form={form}
          productService={productService}
          inventoryTypes={inventoryTypes}
          inventoryAttributeDefinitions={inventoryAttributeDefinitions}
          merchantId={(currentMerchantId || watchedMerchantId) || undefined}
          enabledColumns={enabledOrderItemColumns}
          availableColumns={availableOrderItemColumns}
          onUpdateColumns={onUpdateOrderItemColumns as ((columns: string[], merchantId?: string) => Promise<boolean> | boolean) | undefined}
          role={role}
          inventoryService={activeInventoryService}
          warehouseId={preciseWarehouseId}
          bundleService={bundleService}
          onSelectBundle={handleBundleSelectFromCard}
          searchMerchantId={(currentMerchantId || watchedMerchantId) || undefined}
          bundleGroups={bundleGroups}
          onBundleSetQuantityChange={handleBundleSetQuantityChange}
          onRemoveBundle={handleRemoveBundle}
          multiSelectInventory
        />

        <FloatingActionBarSpacer />
      </div>

      <FloatingActionBar
        role={role}
        size="large"
        onCancel={goBack}
        onSubmit={() => handleSubmit(false)}
        loading={loading}
        disabled={savingAndAdding || preciseModeIncomplete}
        submitText={t('common.button.create_order')}
        submitRef={saveRef}
        extraButtons={
          <>
            <Button
              size="large"
              icon={<PlusOutlined />}
              onClick={handleAddItem}
            >
              {t('shared.orders.form.add_item')}
            </Button>
            <Button
              type="default"
              size="large"
              loading={loading}
              disabled={savingAndAdding || preciseModeIncomplete}
              onClick={() => handleSubmit(true)}
            >
              {t('common.button.save_as_draft')}
            </Button>
            <Button
              size="large"
              loading={savingAndAdding}
              disabled={loading || preciseModeIncomplete}
              onClick={handleSaveAndAddAnother}
            >
              {t('common.button.save_and_add_another')}
            </Button>
          </>
        }
      />
    </Form>
  );
};

export default OrderNew;
